import React, { useContext } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { useHistory } from 'react-router-dom';
import { clearCredentialsSession } from '../../../services/auth/auth-storage';
import { removeUserFromAccount, deleteOwnUser } from '../../../services/apps/UserServices';
import { growlActionSuccess, growlErrors } from '../../../utils/NotificationUtil';
import { NotificationContext } from '../../../components/GrowlNotifications';
import { defineMessages, useIntl } from 'react-intl';

const messages = defineMessages({
  ownerHeader: {
    id: 'user.delete-own-account.owner.header',
    defaultMessage: 'ACCESS DENIED: Transfer of Ownership Required',
  },
  ownerFirstParagraph: {
    id: 'user.delete-own-account.owner.first-paragraph',
    defaultMessage:
      'As the Owner of the corporate GR8PI platform {accountId} ({accountLegalName}), you cannot delete your personal account until you have successfully transferred platform ownership to another active user.',
  },
  ownerSecondParagraph: {
    id: 'user.delete-own-account.owner.second-paragraph',
    defaultMessage: 'To proceed with the deletion of your account, you must first assign a new Owner.',
  },
  ownerConfirmationButtonLabel: {
    id: 'user.delete-own-account.owner.confirmation-button-label',
    defaultMessage: 'Go to ownership transfer',
  },
  regularHeader: {
    id: 'user.delete-own-account.regular.header',
    defaultMessage: 'Deleting Your Account',
  },
  regularFirstParagraph: {
    id: 'user.delete-own-account.regular.first-paragraph',
    defaultMessage:
      'You are about to permanently delete your account. This action will result in the immediate and irrevocable loss of all data, profile information, and access associated with your user.',
  },
  regularSecondParagraph: {
    id: 'user.delete-own-account.regular.second-paragraph',
    defaultMessage: 'This process cannot be reversed.',
  },
  regularThirdParagraph: {
    id: 'user.delete-own-account.regular.third-paragraph',
    defaultMessage: 'Are you sure you wish to proceed?',
  },
  removeMyAccessToThisAccountButtonLabel: {
    id: 'user.delete-own-account.remove-my-access-to-this-account.confirmation-button-label',
    defaultMessage: 'Remove my access to this account',
  },
  deleteMyUserPermanentlyButtonLabel: {
    id: 'user.delete-own-account.delete-my-user-permanently.confirmation-button-label',
    defaultMessage: 'Delete my user entirely',
  },
  cancel: { id: 'actions.cancel', defaultMessage: 'Cancel' },
});

const DeleteOwnAccountConfirmationDialog = ({
  accountId,
  accountLegalName,
  userId,
  userIsOwner,
  visible,
  hide,
  toggleChangeOwner,
}) => {
  const history = useHistory();
  const growl = useContext(NotificationContext);
  const { formatMessage } = useIntl();

  const removeRoleFromAccount = async () => {
    const input = {
      user: userId,
      account: accountId,
    };

    removeUserFromAccount(input)
      .then(() => {
        growlActionSuccess(growl);
        history.push('/');
      })
      .catch((errors) => {
        growlErrors(growl, errors);
      });
  };

  const deleteOwnUserAccount = async () => {
    deleteOwnUser()
      .then(() => {
        growlActionSuccess(growl);
        clearCredentialsSession();
        history.push('/');
      })
      .catch((errors) => {
        growlErrors(growl, errors);
      });
  };

  if (userIsOwner) {
    return (
      <Dialog visible={visible} header={formatMessage(messages.ownerHeader)} onHide={hide}>
        <p>
          {formatMessage(messages.ownerFirstParagraph, {
            accountId,
            accountLegalName,
          })}
        </p>
        <p>{formatMessage(messages.ownerSecondParagraph)}</p>
        <div className="mt20 flex-center">
          <Button
            type="button"
            className="p-button-primary"
            onClick={() => toggleChangeOwner(true)}
            label={formatMessage(messages.ownerConfirmationButtonLabel)}
          />
          <Button type="button" className="p-button-secondary" onClick={hide} label={formatMessage(messages.cancel)} />
        </div>
      </Dialog>
    );
  }

  return (
    <Dialog visible={visible} header={formatMessage(messages.regularHeader)} onHide={hide}>
      <p>{formatMessage(messages.regularFirstParagraph)}</p>
      <p>{formatMessage(messages.regularSecondParagraph)}</p>
      <p>{formatMessage(messages.regularThirdParagraph)}</p>
      <div className="mt20 flex-center">
        <Button type="button" className="p-button-secondary" onClick={hide} label={formatMessage(messages.cancel)} />
        <Button
          type="button"
          className="p-button-warning"
          onClick={removeRoleFromAccount}
          label={formatMessage(messages.removeMyAccessToThisAccountButtonLabel)}
        />
        <Button
          type="button"
          className="p-button-danger"
          onClick={deleteOwnUserAccount}
          label={formatMessage(messages.deleteMyUserPermanentlyButtonLabel)}
        />
      </div>
    </Dialog>
  );
};

export default DeleteOwnAccountConfirmationDialog;
